import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { api } from "./api";

/* ------------------------------ Cart Store ------------------------------ */
const CartCtx = createContext(null);
const CART_KEY = "pf_cart";

// Simple debounce for cart auto-persist to backend
let _saveTimer = null;

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(CART_KEY) || "[]");
    } catch { return []; }
  });
  const [open, setOpen] = useState(false);
  // Set by AuthProvider once we know if the user is authenticated
  const [isAuthed, setIsAuthed] = useState(false);

  useEffect(() => {
    localStorage.setItem(CART_KEY, JSON.stringify(items));
    // Auto-persist to backend when logged in (debounced)
    if (isAuthed) {
      clearTimeout(_saveTimer);
      _saveTimer = setTimeout(() => {
        api.put("/cart", { items }).catch(() => {});
      }, 600);
    }
  }, [items, isAuthed]);

  const addItem = useCallback((product, size_ml, qty = 1) => {
    const size = product.sizes.find((s) => s.ml === size_ml);
    if (!size) return;
    setItems((prev) => {
      const idx = prev.findIndex((i) => i.product_id === product.id && i.size_ml === size_ml);
      if (idx >= 0) {
        const next = [...prev];
        next[idx].qty += qty;
        return next;
      }
      return [...prev, {
        product_id: product.id,
        slug: product.slug,
        name: product.name,
        brand: product.brand,
        image: product.images[0],
        size_ml,
        price: size.price,
        qty,
      }];
    });
    setOpen(true);
  }, []);

  const updateQty = (product_id, size_ml, qty) =>
    setItems((prev) => prev
      .map((i) => (i.product_id === product_id && i.size_ml === size_ml ? { ...i, qty } : i))
      .filter((i) => i.qty > 0));

  const removeItem = (product_id, size_ml) =>
    setItems((prev) => prev.filter((i) => !(i.product_id === product_id && i.size_ml === size_ml)));

  const clear = () => setItems([]);

  /** Called by AuthProvider after a successful login. Sends the guest cart
   *  to /cart/sync (which sums quantities with the saved cart) and replaces
   *  local state with the merged result. */
  const syncOnLogin = useCallback(async (localItems) => {
    try {
      const { data } = await api.post("/cart/sync", { items: localItems });
      setItems(data.items || []);
      setIsAuthed(true);
      return data.items || [];
    } catch {
      setIsAuthed(true);
      return localItems;
    }
  }, []);

  const subtotal = items.reduce((s, i) => s + i.price * i.qty, 0);
  const count = items.reduce((s, i) => s + i.qty, 0);

  return (
    <CartCtx.Provider value={{
      items, addItem, updateQty, removeItem, clear, subtotal, count,
      open, setOpen, syncOnLogin, setIsAuthed, setItems,
    }}>
      {children}
    </CartCtx.Provider>
  );
};

export const useCart = () => useContext(CartCtx);

/* ------------------------------ Auth Store ------------------------------ */
const AuthCtx = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const cart = useContext(CartCtx);
  const bootstrapped = useRef(false);   // guard against StrictMode double effect

  const afterLogin = useCallback(async (nextUser) => {
    setUser(nextUser);
    if (cart) {
      // Explicit login: merge guest local cart with saved profile cart (sum qtys)
      let guestItems = [];
      try { guestItems = JSON.parse(localStorage.getItem(CART_KEY) || "[]"); } catch {}
      await cart.syncOnLogin(guestItems);
    }
  }, [cart]);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      // Session restore. IMPORTANT: load server cart BEFORE marking as authed,
      // so the auto-persist effect doesn't PUT stale local items to the server.
      if (cart) {
        try {
          const { data: c } = await api.get("/cart");
          if (Array.isArray(c.items)) cart.setItems(c.items);
        } catch {}
        cart.setIsAuthed(true);
      }
    } catch {
      setUser(null);
      if (cart) cart.setIsAuthed(false);
    } finally {
      setLoading(false);
    }
  }, [cart]);

  useEffect(() => {
    if (bootstrapped.current) return;
    bootstrapped.current = true;
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loginLocal = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("pf_token", data.token);
    await afterLogin(data.user);
    return data.user;
  };

  const registerLocal = async (email, password, name) => {
    const { data } = await api.post("/auth/register", { email, password, name });
    localStorage.setItem("pf_token", data.token);
    await afterLogin(data.user);
    return data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("pf_token");
    setUser(null);
    if (cart) cart.setIsAuthed(false);
  };

  return (
    <AuthCtx.Provider value={{ user, loading, loginLocal, registerLocal, logout, setUser, checkAuth, afterLogin }}>
      {children}
    </AuthCtx.Provider>
  );
};

export const useAuth = () => useContext(AuthCtx);

/* ------------------------------ Wishlist -------------------------------- */
const WISH_KEY = "pf_wishlist";
export const useWishlist = () => {
  const [ids, setIds] = useState(() => {
    try { return JSON.parse(localStorage.getItem(WISH_KEY) || "[]"); } catch { return []; }
  });
  useEffect(() => localStorage.setItem(WISH_KEY, JSON.stringify(ids)), [ids]);
  const toggle = (id) => setIds((prev) => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const has = (id) => ids.includes(id);
  return { ids, toggle, has };
};
